(function(angular, _) {
    angular.module('wtApp.dataService')
        .factory('WtObservable', ['WtObserver', (WtObserver) => {
            /**
             * @ngdoc service
             * @name WtObservable
             * @description Observable interface implementation from the Observable pattern.
             * It is a "hot observable" implementation, meaning that if the observer is late to the party, it misses all the fun.
             * The initiative is to replace the current $rootScope.$on event model with the observable pattern.
             * This should allow us to get rid of the scope and move towards Angular upgrade.
             * When the Observables land in the standard implementation of ES the Webtrekk implementation will be effortlessly dropped.
             */
            return class WtObservable {
                /**
                 * @ngdoc service
                 * @name WtObservable
                 * @description
                 * @param {Function} subscribe The source that emits the changes that are pushed to the observable subscribers
                 */
                constructor(subscribe) {
                    if (!_.isFunction(subscribe)) {
                        throw new Error('The WtObservable should be initialized with a subscribe function');
                    }

                    /**
                     * The observers that are currently subscribed to the current instance of Observable
                     * @type {Array}
                     */
                    this.observers = [];

                    /**
                     * Subscribes to the observable source and propagates the events to the observers
                     * Can return an object with unsubscribe method that can be called to cleanup once the observable is destroyed or nothing
                     * Uses a shallow copy of the observers array to avoid running into the issue when the observers unsubscribe during the
                     * iteration mutating the array we iterate on
                     * @type {undefined|Object}
                     */
                    this.subscription = subscribe({
                        next: (value) => _.map(this.observers.slice(), (observer) => observer.next(value)),
                        error: (err) => {
                            _.map(this.observers.slice(), (observer) => observer.error(err));
                            this.cleanup_();
                        },
                        complete: () => {
                            _.map(this.observers.slice(), (observer) => observer.complete());
                            this.cleanup_();
                        }
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtObservable#subscribe
                 * @description Subscribes to the observable changes. For arguments see the constructor of WtObserver.
                 * @example
                 *  ```
                 *  const subscription = observable.subscribe((value) => {console.log(value);});
                 *  subscription.unsubscribe();
                 *  ```
                 * @returns {{unsubscribe: (function())}}
                 */
                subscribe() {
                    const wtObserver = new WtObserver(...arguments);

                    this.observers.push(wtObserver);

                    return {
                        unsubscribe: () => {
                            wtObserver.unsubscribe();

                            this.observers.splice(
                                _.indexOf(this.observers, wtObserver), 1
                            );
                        }
                    };
                }

                /**
                 * @ngdoc method
                 * @name WtObservable#filter
                 * @description Filters the values emitted by the observable
                 * @param {Function} fn The filter function
                 * @example
                 *  ```
                 *  const subscription = observable
                 *      .filter((value) => {
                 *          return Boolean(value);
                 *      })
                 *      .subscribe((value) => {
                 *          console.log(value); // Subscribes only for truthy values
                 *      });
                 *  subscription.unsubscribe();
                 *  ```
                 * @returns {WtObservable}
                 */
                filter(fn) {
                    if (!_.isFunction(fn)) {
                        throw new Error(`Expecting a filter function in WtObservable, got ${typeof fn}`);
                    }

                    return new WtObservable((observer) => this.subscribe({
                        next(value) {
                            /* eslint consistent-return:0 */
                            if (observer.unsubscribed) {
                                return;
                            }

                            try {
                                if (!fn(value)) {
                                    return;
                                }
                            } catch (error) {
                                return observer.error(error);
                            }

                            return observer.next(value);
                        },

                        error(error) {
                            return observer.error(error);
                        },

                        complete() {
                            return observer.complete();
                        }
                    }));
                }

                /**
                 * @ngdoc method
                 * @name WtObservable#cleanup_
                 * @description Cleanup on complete or error
                 * @private
                 */
                cleanup_() {
                    this.observers = [];

                    if (_.isFunction(this.subscription)) {
                        this.subscription();
                    } else if (_.isFunction(_.get(this.subscription, 'unsubscribe'))) {
                        this.subscription.unsubscribe();
                    }
                }
            };
        }]);
})(window.angular, window._);
